import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GAHAppResponse } from '@data/schema/gah-app-response';
import { environment } from '@environments/environment';
import { ApiPaths } from '@gah-core/constants/api-paths.enum';
import { UserStoreService } from '@gah-core/services/user-store.service';
import { MetadataTerritory } from '@modules/admin/manage-assets/models/metadata-territories.model';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppTranslation } from '../models/app-translations.model';
import { HomePageHeaderMedia } from '../models/homepage-media.model';

@Injectable({
  providedIn: 'root',
})
export class ManageHomepageService {
  constructor(public http: HttpClient, private userStoreService: UserStoreService) {}

  apiUrl = environment.apiUrl;
  private readonly releaseScheduleObservable = new BehaviorSubject<any>(null);

  dateFormatCreds: {
    territoryId: string;
    dateFormat: string;
    releaseWindow: string;
    idGroupTerritory: string;
    territoryActualInd: string;
    groupTypeCode: string;
  };

  userGuideInfo: {
    groupTerritoryId: number;
    userLoginId: string;
    fileKey: string;
    fileType: string;
  };

  emailHeader: {
    imgFileName: string;
    groupTypeCode: string;
    territoryId: number;
    groupsTerritoryId: number
  };


  assetFormatCreds: {
    assetData: HomePageHeaderMedia[];
    removedAssetData: HomePageHeaderMedia[];
    groupTerritoryId: number;
    contactDirectory: any;
    removedContactDirectory: any;
  };

  runnerHTTPUploadItem: {
    file: File;
    groupsTerritoryId: number;
    userName: string;
    territoryName: string;
    groupTypeCode: string;
  };

  /**
   * @function Makes an api call to update Date format for the specified territory
   */

  updateDateFormat(credentials: {
    territoryId: string;
    dateFormat: string;
    releaseWindow: string;
    idGroupTerritory: string;
    territoryActualInd: string;
  }): Observable<any> | null {
    this.dateFormatCreds = {
      territoryId: credentials.territoryId,
      dateFormat: credentials.dateFormat,
      releaseWindow: credentials.releaseWindow,
      idGroupTerritory: credentials.idGroupTerritory,
      territoryActualInd: credentials.territoryActualInd,
      groupTypeCode: this.userStoreService.userState.selectedTerritory?.groupTypeCode

    };
    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.UpdateDateFormatTerritory}`, this.dateFormatCreds)
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateHomePageHeaderMediaDisplayOrder(
    homePageMediaAssets: HomePageHeaderMedia[] = []
  ): Observable<any> | null {
    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.updateExistingAssetDisplayOrder}`, homePageMediaAssets)
      .pipe(catchError((error) => this.handleError(error)));
  }

  fetchHomePageHeaderMedia(groupTerritoryId: number) {
    let params = new HttpParams()
      .set('groupTerritoryId', groupTerritoryId)
      .set('groupsTerritoryId', this.userStoreService.userState.groupsTerritoryId);
    return this.http
      .get<any>(`${this.apiUrl}${ApiPaths.fetchHomePageHeaderMediaAssets}`, { params })
      .pipe(
        map((res: GAHAppResponse) => {
          return res.serviceResult;
        })
      );
  }

  fetchContactDirectoryData(groupTerritoryId: number) {
    let params = new HttpParams()
      .set('groupTerritoryId', groupTerritoryId)
      .set('groupsTerritoryId', this.userStoreService.userState.groupsTerritoryId);
    return this.http
      .get<any>(`${this.apiUrl}${ApiPaths.fetchContactDirectoryData}`, { params })
      .pipe(
        map((res: GAHAppResponse) => {
          return res.serviceResult;
        })
      );
  }

  checkITSupportAccess() {
    let params = new HttpParams().set('emailAddress', this.userStoreService.userState.emailAddress);
    return this.http.get<any>(`${this.apiUrl}${ApiPaths.CheckITSupportAccess}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        return res.serviceResult;
      })
    );
  }

  saveOrRemoveHomepageHeaderMedia(
    homePageAssetArr: {
      homePageAssets: HomePageHeaderMedia[];
    },
    removedhomePageAssetArr: {
      removedhomePageAssets: HomePageHeaderMedia[];
    },
    contactDirectory,
    removedContactDirectory
  ): Observable<any> | null {
    this.assetFormatCreds = {
      assetData: homePageAssetArr.homePageAssets,
      removedAssetData: removedhomePageAssetArr.removedhomePageAssets,
      groupTerritoryId: this.userStoreService.userState.groupsTerritoryId,
      contactDirectory: contactDirectory,
      removedContactDirectory: removedContactDirectory
    };

    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.saveHomePageHeaderMediaAssets}`, this.assetFormatCreds)
      .pipe(
        map((res: GAHAppResponse) => {
          return res;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * @function To fetch the runner folder Ids for Upload/Import media settings
   * @param groupTypeCode
   */
  fetchRunnerMediaInfo(groupTypeCode: string): Observable<any> | null {
    let params = new HttpParams()
      .set('groupTypeCode', groupTypeCode)
      .set('territoryId', this.userStoreService.userState.selectedTerritory.territoryId);
    return this.http
      .get<any>(`${this.apiUrl}${ApiPaths.FetchRunnerInfo}`, { params })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * @function To update the runner media info
   */
  updateRunnerMediaInfo(credentials: {
    groupsRunnerFolderList: {
      userId: number;
      groupTypeCode: string;
      folderId: number;
      folderTypeCode: string;
      loginId: string;
      gahGroupsRunnerFoldersId: number;
    }[];
    groupsTerrDfltsList: {
      activeInd: number;
      groupsTerrDfltsCode: string;
      groupsTerrDfltsId: number;
      groupsTerrDfltsValue: string;
      groupsTerritoryId: number;
      userId: number;
    }[];
  }): Observable<any> | null {
    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.UpdateRunnerInfo}`, credentials)
      .pipe(catchError((error) => this.handleError(error)));
  }

  getAssetLocalTitles(isAllActiveTitlesNames?: boolean): Observable<any> | null {
    let params = new HttpParams()
      .set('territoryId', this.userStoreService.userState.selectedTerritory.territoryId ?? 7)
      .set('groupsTerritoryId', this.userStoreService.userState.groupsTerritoryId)
      .set('groupTypeCode', this.userStoreService.userState.selectedTerritory.groupTypeCode ?? 'E')
      .set('isAllActiveTitlesNames', isAllActiveTitlesNames ?? false);

    return this.http.get(`${environment.apiUrl}${ApiPaths.AssetsLocalTitleNames}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        return res;
      }),
      catchError((error) => this.handleError(error))
    );
  }

  getExistingAssets(runnerIdList: string, page: number): Observable<any> | null {
    let params = new HttpParams()
      .set('territoryId', this.userStoreService.userState.selectedTerritory.territoryId ?? 7)
      .set('groupTypeCode', this.userStoreService.userState.selectedTerritory.groupTypeCode ?? 'E')
      .set('isHomePage', true)
      .set('page', page)
      .set('groupsTerritoryId', this.userStoreService.userState.groupsTerritoryId)
      .set('runnerIds', runnerIdList);
    return this.http
      .get(`${environment.apiUrl}${ApiPaths.AssetsSearchExistingAssets}`, { params })
      .pipe(
        map((response: GAHAppResponse) => {
          return response;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  titleDetails: {
    releaseId: number;
    fileName: any;
    territoryId: number;
    groupTypeCode: string;
  };

  getFilterAssets(
    credential: {
      title: number;
      fileName: any;
    },
    page: number,
    runnerIdList: string
  ): Observable<any> | null {
    this.titleDetails = {
      releaseId: credential.title,
      fileName: credential.fileName,

      territoryId: this.userStoreService.userState.selectedTerritory.territoryId ?? 7,
      groupTypeCode: this.userStoreService.userState.selectedTerritory.groupTypeCode ?? 'E',
    };
    let params = new HttpParams()
      .set('territoryId', this.userStoreService.userState.selectedTerritory.territoryId ?? 7)
      .set('fileName', this.titleDetails.fileName)
      .set('releaseId', this.titleDetails.releaseId)
      .set('isHomePage', true)
      .set('page', page)
      .set('runnerIds', runnerIdList)
      .set('groupsTerritoryId', this.userStoreService.userState.groupsTerritoryId)
      .set('groupTypeCode', this.userStoreService.userState.selectedTerritory.groupTypeCode ?? 'E');

    return this.http
      .get(`${environment.apiUrl}${ApiPaths.AssetsSearchExistingAssets}`, { params })
      .pipe(
        map((res: GAHAppResponse) => {
          if (res.serviceResult != '') {
            return res;
          }
        }),
        catchError((error) => this.handleError(error))
      );
  }

  existingTitleDetails: {
    fileName: any;
  };

  getExistingHeaderMedia(
    filename: string,
    page: number,
    size: number,
    runnerIdList: string
  ): Observable<any> | null {
    let params = new HttpParams()
      .set('fileName', filename)
      .set('groupTypeCode', this.userStoreService.userState.selectedTerritory.groupTypeCode ?? 'E')
      .set('runnerIds', runnerIdList)
      .set('mediaTypeCode', 'Home Header Media Assets')
      .set('page', page)
      .set('size', size);

    return this.http.get(`${environment.apiUrl}${ApiPaths.AssetsExistingMedia}`, { params }).pipe(
      map((response: GAHAppResponse) => {
        return response;
      }),
      catchError((error) => this.handleError(error))
    );
  }

  fetchNotificationEmails(): Observable<any> | null {
    let params = new HttpParams().set(
      'groupTerritoryId',
      this.userStoreService.userState.groupsTerritoryId
    );
    return this.http
      .get<any>(`${environment.apiUrl}${ApiPaths.FetchEmailNotifications}`, { params })
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateNotificationEmails(creds: any): Observable<any> | null {
    let obj: {
      groupsTerritoryId: number;
      groupsTerrDfltsDTOList: {
        activeInd: number;
        groupsTerrDfltsCode: string;
        groupsTerrDfltsId: number;
        groupsTerrDfltsValue: string;
        groupsTerritoryId: number;
        userId: number;
        loginId: string;
      }[];
    } = {
      groupsTerritoryId: this.userStoreService.userState.groupsTerritoryId,
      groupsTerrDfltsDTOList: creds,
    };
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.UpdateEmailNotifications}`, obj)
      .pipe(catchError((error) => this.handleError(error)));
  }
  private handleError(error: HttpErrorResponse) {
    let errorMsg: {
      message: string;
      errorCode: number;
    } = {
      message: error.error.errorDescription,
      errorCode: error.status,
    };
    return throwError(errorMsg);
  }

  UploadUserGuide(
    file: File,
    gruupTrrId: string,
    userName: string,
    territory: string,
    fileType: string
  ): Observable<any> | null {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('groupTerritoryId', gruupTrrId);
    formData.append('userName', userName);
    formData.append('territory', territory);
    formData.append('fileType', fileType);
    return this.http.post<any>(`${this.apiUrl}${ApiPaths.UploadUserGuide}`, formData).pipe(
      map((res: GAHAppResponse) => {
        if (res.serviceResult == null) {
          return null;
        }
        if (fileType == 'RS') {
          this.releaseScheduleObservable.next(true);
        }
        const { fileKey, fileSize } = res.serviceResult;
        return { fileKey, fileSize };
      }),
      catchError((error) => this.handleError(error))
    );
  }

  getReleaseScheduleObservable() {
    return this.releaseScheduleObservable.asObservable();
  }

  removeUserGuide(
    fileName: string,
    loginUserId: string,
    groupTerritoryId: number,
    fileType: string
  ): Observable<any> | null {
    this.userGuideInfo = {
      fileKey: fileName,
      groupTerritoryId: groupTerritoryId,
      userLoginId: loginUserId,
      fileType: fileType,
    };

    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.RemoveUserGuide}`, this.userGuideInfo)
      .pipe(
        map((res: GAHAppResponse) => {
          if (res != null && res.serviceResult != null && fileType == 'RS') {
            this.releaseScheduleObservable.next(false);
          }
          return res;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  fetchUserGuide(groupTerritoryId: number, userLoginId: string, fileType: string) {
    let params = new HttpParams()
      .set('groupTerritoryId', groupTerritoryId)
      .set('userLoginId', userLoginId)
      .set('fileType', fileType);
    return this.http.get<any>(`${this.apiUrl}${ApiPaths.FetchUserGuide}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        if (res.serviceResult != null) this.releaseScheduleObservable.next(true);
        else this.releaseScheduleObservable.next(false);
        return res.serviceResult;
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Function - Fetch Email Header (GAHR324)
   * @param territoryName
   * @param groupTypeCode
   * @returns
   */
  fetchEmailHeader(territoryId: number, groupTypeCode: string) {
    let params = new HttpParams()
      .set('territoryId', territoryId.toString())
      .set('groupTypeCode', groupTypeCode);
    return this.http.get<any>(`${this.apiUrl}${ApiPaths.EmailHeaderFetch}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        return res.serviceResult;
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Function - Upload Email Header (GAHR324)
   * @param file
   * @param groupTerritoryId
   * @param userName
   * @param territory
   * @param groupTypeCode
   * @returns
   */
  uploadEmailHeader(
    file: File,
    territoryId: number,
    userName: string,
    territory: string,
    groupTypeCode: string
  ): Observable<any> | null {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('territoryId', territoryId.toString());
    formData.append('userName', userName);
    formData.append('territory', territory);
    formData.append('groupTypeCode', groupTypeCode);

    return this.http.post<any>(`${this.apiUrl}${ApiPaths.EmailHeaderUpload}`, formData).pipe(
      map((res: GAHAppResponse) => {
        if (res.serviceResult == null) {
          return null;
        }
        const { fileName, fileSize } = res.serviceResult;
        return { fileName, fileSize };
      }),
      catchError((error) => this.handleError(error))
    );
  }

  updateTranslations(dirtyTranslations: AppTranslation[]) {
    return this.http
      .post(`${environment.apiUrl}${ApiPaths.updateTranslations}`, dirtyTranslations)
      .pipe(
        map((response: GAHAppResponse) => {
          return response;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  getExtention(ext) {
    let type = undefined;
    switch (ext) {
      case 'txt':
        type = 'text/plain';
        break;
      case 'pdf':
        type = 'application/pdf';
        break;
      case 'doc':
        type = 'application/msword';
        break;
      case 'docx':
        type = 'application/msword';
        break;
      default:
    }
    return type;
  }

  getUserGuideFile(groupTerritoryId: number, fileType: string): Observable<any> | null {
    let params = new HttpParams()
      .set('groupTerritoryId', groupTerritoryId)
      .set('fileType', fileType);
    return this.http.get<any>(`${this.apiUrl}${ApiPaths.GetUserGuideFile}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        const byteArray = new Uint8Array(
          atob(res.serviceResult)
            .split('')
            .map((char) => char.charCodeAt(0))
        );
        return new Blob([byteArray], { type: 'application/pdf' });
      }),
      catchError((error) => this.handleError(error))
    );
  }

  getAllTranslations() {
    let params = new HttpParams().set(
      'territoryId',
      this.userStoreService.userState.selectedTerritory.territoryId
    );
    return this.http.get(`${environment.apiUrl}${ApiPaths.fetchTranslations}`, { params }).pipe(
      map((response: GAHAppResponse) => {
        return response;
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Function - Remove email header (GAHR-357)
   * @param fileName
   * @param groupTypeCode
   * @param territoryId
   * @returns
   */
  removeEmailHeader(
    fileName: string,
    groupTypeCode: string,
    territoryId: number,
    groupsTerritoryId: number
  ): Observable<any> | null {
    this.emailHeader = {
      imgFileName: fileName,
      groupTypeCode: groupTypeCode,
      territoryId: territoryId,
      groupsTerritoryId: groupsTerritoryId
    };
    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.RemoveEmailHeader}`, this.emailHeader)
      .pipe(catchError((error) => this.handleError(error)));
  }

  loadAssetsToOpenSearch(titleIds: number[], territoryId: number, groupTypeCode: string) {
    return this.http
      .post(`${environment.apiUrl}${ApiPaths.LoadAssetsToOpenSearch}`, {
        titleIds: titleIds,
        territoryId: territoryId,
        groupTypeCode: groupTypeCode,
        groupTerritoryId: this.userStoreService.userState?.groupsTerritoryId
      })
      .pipe(
        map((response: GAHAppResponse) => {
          return response;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  loadTitlesToOpenSearch(titleIds: number[], territoryId: number, groupTypeCode: string) {
    return this.http
      .post(`${environment.apiUrl}${ApiPaths.LoadTitlesToOpenSearch}`, {
        titleIds: titleIds,
        territoryId: territoryId,
        groupTypeCode: groupTypeCode,
        groupTerritoryId: this.userStoreService.userState?.groupsTerritoryId
      })
      .pipe(
        map((response: GAHAppResponse) => {
          return response;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Makes http GET request to get user details using userid
   *
   * @param userId userid of the selected user
   * @returns response as observable
   */
  getUserTerritories(userId: number, territoryId: number, groupRoleId: number): Observable<MetadataTerritory[]> {
    let params = new HttpParams().set('userId', userId).set('territoryId', territoryId).
    set('groupRoleId', groupRoleId);
    return this.http.get(`${environment.apiUrl}${ApiPaths.UserDetailsById}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        let availableTerritories: MetadataTerritory[] = (
          res.serviceResult.usersGroupsTerritories as any[]
        ).map<MetadataTerritory>((territory) => {
          return {
            territoryId: territory.territory.territoryId,
            territoryNameWithGroup: `[${this.parseGroupTypeCode(territory.groupTypeCode)}] ${territory.territory.territoryName
            }`,
            territoryName: territory.territory.territoryName,
            group: this.parseGroupTypeCode(territory.groupTypeCode),
            groupTypeCode: territory.groupTypeCode,
          };
        });
        return availableTerritories;
      })
    );
  }

  /**
   * Accepts group type code and returns group type name
   *
   * @param code Group type code ('E' or 'P')
   * @returns Group type label
   */
  parseGroupTypeCode(code: string) {
    return code === 'E' ? 'Exhibitor Relations' : 'Publicity';
  }

  /**
   * Function - Fetch group territory release window
   * @param territoryName
   * @param groupTypeCode
   * @returns
   */
  fetchTerritoryReleaseWindow(groupTerritoryId: number): Observable<any> | null {
    let params = new HttpParams()
      .set('groupTerritoryId', groupTerritoryId);
    return this.http
      .get<any>(`${this.apiUrl}${ApiPaths.getTerritoryReleaseWindow}`, { params })
      .pipe(catchError((error) => this.handleError(error)));
  }
}
